// @ts-check
// Colunas de sub-campo na tabela (contrato 08-contrato-kanban-tabela, seção "Colunas de sub-campo"): uma viewField de
// endereço pode se mostrar como várias colunas (CEP, bairro, cidade...), guardadas como lista JSON na própria
// viewField, porque a tabela view_field tem um registro por campo. subFieldName null é o endereço inteiro. Módulo puro,
// usado pelo motor (validação) e pela SPA (colunas). Extensão do appcrm: o Twenty não tem coluna por sub-campo.
import { getFieldTypeDefinition } from '../field-types/field-type-definitions.js';

/** @typedef {{ subFieldName: string | null, size: number, aggregateOperation: string | null }} SubFieldColumn */

export const SUB_FIELD_COLUMN_NAMES_BY_TYPE = Object.freeze({
  ADDRESS: Object.freeze(['addressPostcode', 'addressStreet1', 'addressStreet2', 'addressCity', 'addressState']),
});

// largura padrão de cada parte; a chave vazia é o endereço inteiro
export const DEFAULT_SUB_FIELD_COLUMN_SIZES = Object.freeze({
  addressPostcode: 110, addressStreet1: 220, addressStreet2: 160, addressCity: 140, addressState: 70, '': 240,
});

export const MAXIMUM_SUB_FIELD_COLUMNS = 6;
export const SUB_FIELD_COLUMN_MIN_SIZE = 60;
export const SUB_FIELD_COLUMN_MAX_SIZE = 1000;

/** @param {string | null | undefined} fieldType */
export function columnSubFieldNamesOf(fieldType) {
  return /** @type {ReadonlyArray<string>} */ (SUB_FIELD_COLUMN_NAMES_BY_TYPE[/** @type {keyof typeof SUB_FIELD_COLUMN_NAMES_BY_TYPE} */ (fieldType ?? '')] ?? []);
}

/** @param {string | null | undefined} subFieldName */
export function defaultSubFieldColumnSize(subFieldName) {
  return DEFAULT_SUB_FIELD_COLUMN_SIZES[/** @type {keyof typeof DEFAULT_SUB_FIELD_COLUMN_SIZES} */ (subFieldName ?? '')] ?? DEFAULT_SUB_FIELD_COLUMN_SIZES[''];
}

/**
 * Lista de partes normalizada, ou o motivo da recusa (em inglês, como os outros erros de validação de view).
 * @param {string | null | undefined} fieldType
 * @param {unknown} value
 * @param {string} [fieldName] nome do campo para a mensagem
 * @returns {{ value: SubFieldColumn[] | null, error?: undefined } | { value?: undefined, error: string }}
 */
export function normalizeSubFieldColumns(fieldType, value, fieldName = 'field') {
  if (value === undefined || value === null) return { value: null };
  const allowed = columnSubFieldNamesOf(fieldType);
  if (allowed.length === 0) return { error: `subFieldColumns is only available for ${Object.keys(SUB_FIELD_COLUMN_NAMES_BY_TYPE).join(', ')} fields` };
  if (!Array.isArray(value)) return { error: 'subFieldColumns must be a list' };
  if (value.length === 0) return { value: null };
  if (value.length > MAXIMUM_SUB_FIELD_COLUMNS) return { error: `subFieldColumns has at most ${MAXIMUM_SUB_FIELD_COLUMNS} items` };
  const operations = getFieldTypeDefinition(/** @type {string} */ (fieldType)).aggregateOperations;
  /** @type {SubFieldColumn[]} */
  const columns = [];
  const seen = new Set();
  for (const item of value) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return { error: 'subFieldColumns items must be objects' };
    const subFieldName = item.subFieldName ?? null;
    if (subFieldName !== null && (typeof subFieldName !== 'string' || !allowed.includes(subFieldName))) {
      return { error: `'${String(subFieldName)}' is not a column sub-field of '${fieldName}'` };
    }
    if (seen.has(subFieldName)) return { error: `subFieldColumns repeats '${subFieldName ?? 'null'}'` };
    seen.add(subFieldName);
    const size = item.size ?? defaultSubFieldColumnSize(subFieldName);
    if (!Number.isInteger(size) || size < SUB_FIELD_COLUMN_MIN_SIZE || size > SUB_FIELD_COLUMN_MAX_SIZE) {
      return { error: `subFieldColumns size must be an integer between ${SUB_FIELD_COLUMN_MIN_SIZE} and ${SUB_FIELD_COLUMN_MAX_SIZE}` };
    }
    const aggregateOperation = item.aggregateOperation ?? null;
    // rodapé da parte: contar todos ou as operações do tipo, como no campo inteiro
    if (aggregateOperation !== null && aggregateOperation !== 'COUNT' && !operations.includes(aggregateOperation)) {
      return { error: `subFieldColumns aggregateOperation '${String(aggregateOperation)}' is not available for '${fieldName}' (${fieldType})` };
    }
    columns.push({ subFieldName, size, aggregateOperation });
  }
  return { value: columns };
}

/**
 * Lê a coluna JSON guardada; texto inválido vira null (a viewField volta a ser uma coluna só).
 * @param {unknown} stored
 * @returns {SubFieldColumn[] | null}
 */
export function readStoredSubFieldColumns(stored) {
  if (typeof stored !== 'string' || stored === '') return null;
  try {
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch {
    return null;
  }
}
